/**
 * SimulacionContext.jsx
 * 
 * Context para compartir el estado de la simulación entre componentes
 * Permite que la página de seguimiento acceda a la posición del repartidor en tiempo real
 * 
 * Qué hace:
 * - Almacena el estado de la simulación (posición del repartidor, ruta, puntos de entrega)
 * - Permite que múltiples componentes accedan al mismo estado
 * - Genera IDs únicos para cada punto de entrega para los enlaces de seguimiento
 */

import React, { createContext, useContext, useState, useRef, useEffect } from 'react'

const SimulacionContext = createContext()

/**
 * Provider del contexto de simulación
 */
export function SimulacionProvider({ children }) {
  // Cargar estado inicial desde sessionStorage si existe
  const cargarDesdeStorage = () => {
    try {
      const stored = localStorage.getItem('turutatrack_simulacion')
      if (stored) {
        const data = JSON.parse(stored)
        console.log('🔵 cargarDesdeStorage - Datos encontrados en storage:', {
          puntosCount: data.puntosEntrega?.length || 0,
          tieneRuta: !!data.ruta,
          idsCount: Object.keys(data.idsEntrega || {}).length,
          rawData: data
        })
        return {
          puntosEntrega: data.puntosEntrega || [],
          ruta: data.ruta || [],
          puntoInicial: data.puntoInicial || null,
          idsEntrega: data.idsEntrega || {},
          posicionRepartidor: data.posicionRepartidor || null,
          simulando: data.simulando || false
        }
      } else {
        console.log('🔵 cargarDesdeStorage - No hay datos en storage')
        
        // Debug: verificar todas las claves en localStorage
        const allKeys = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          allKeys.push(key)
        }
        console.log('🔍 Todas las claves en localStorage:', allKeys)
        
        // Intentar leer directamente
        const directRead = localStorage.getItem('turutatrack_simulacion')
        console.log('🔍 Lectura directa de turutatrack_simulacion:', {
          value: directRead,
          isNull: directRead === null,
          isUndefined: directRead === undefined,
          type: typeof directRead
        })
      }
    } catch (error) {
      console.error('❌ Error cargando desde sessionStorage:', error)
    }
    return {
      puntosEntrega: [],
      ruta: [],
      puntoInicial: null,
      idsEntrega: {}
    }
  }

  const datosIniciales = cargarDesdeStorage()
  
  // Debug: verificar qué se carga inicialmente
  console.log('🔵 SimulacionContext inicializando con datos:', {
    puntosCount: datosIniciales.puntosEntrega?.length || 0,
    tieneRuta: !!datosIniciales.ruta,
    idsCount: Object.keys(datosIniciales.idsEntrega || {}).length
  })

  // Estado de la simulación
  const [posicionRepartidor, setPosicionRepartidor] = useState(datosIniciales.posicionRepartidor || null)
  const [ruta, setRuta] = useState(datosIniciales.ruta || [])
  const [puntosEntrega, setPuntosEntrega] = useState(datosIniciales.puntosEntrega || [])
  const [puntoInicial, setPuntoInicial] = useState(datosIniciales.puntoInicial || null)
  const [simulando, setSimulando] = useState(datosIniciales.simulando || false)
  const [velocidad, setVelocidad] = useState(50) // km/h
  
  // IDs únicos para cada punto de entrega (para enlaces de seguimiento)
  const idsEntregaRef = useRef(new Map())
  
  // Flag para evitar que el useEffect sobrescriba en la primera ejecución
  const primeraEjecucionRef = useRef(true)
  
  // Cargar IDs desde storage al inicializar
  useEffect(() => {
    if (datosIniciales.idsEntrega && Object.keys(datosIniciales.idsEntrega).length > 0) {
      // Convertir el objeto de IDs a Map
      Object.entries(datosIniciales.idsEntrega).forEach(([index, id]) => {
        idsEntregaRef.current.set(parseInt(index), id)
      })
      console.log('🔵 IDs cargados en el Map:', Object.fromEntries(idsEntregaRef.current))
    }
    
    // Marcar que la primera ejecución ya pasó después de un breve delay
    setTimeout(() => {
      primeraEjecucionRef.current = false
    }, 100)
  }, []) // Solo al montar

  // Guardar en sessionStorage cuando cambien los datos importantes
  useEffect(() => {
    try {
      // NO ejecutar en la primera renderización si el estado está vacío
      // Esto previene que se sobrescriban los datos cuando se abre una nueva pestaña
      if (primeraEjecucionRef.current) {
        if (!puntosEntrega || puntosEntrega.length === 0) {
          // En la primera ejecución, si el estado está vacío, no hacer nada
          // Los datos se cargarán desde storage en la inicialización
          console.log('🟡 Primera ejecución - estado vacío, no guardando')
          return
        }
      }
      
      // SIEMPRE verificar primero qué hay en storage
      const stored = localStorage.getItem('turutatrack_simulacion')
      let dataEnStorage = null
      if (stored) {
        try {
          dataEnStorage = JSON.parse(stored)
        } catch (e) {
          console.error('Error parseando storage:', e)
        }
      }
      
      // CRÍTICO: Si el estado está vacío pero storage tiene datos, NO ejecutar este useEffect
      // Esto previene que se sobrescriban los datos cuando se abre una nueva pestaña
      if (!puntosEntrega || puntosEntrega.length === 0) {
        if (dataEnStorage?.puntosEntrega && dataEnStorage.puntosEntrega.length > 0) {
          // El estado está vacío pero storage tiene datos - NO hacer nada
          console.log('🟡 NO ejecutando useEffect - estado vacío pero storage tiene datos:', {
            puntosEnStorage: dataEnStorage.puntosEntrega.length,
            puntosEnEstado: puntosEntrega?.length || 0,
            idsEnStorage: Object.keys(dataEnStorage.idsEntrega || {}).length
          })
          return // NO ejecutar el setItem, mantener los datos de storage intactos
        }
      }
      
      // Si llegamos aquí, el estado tiene datos o storage está vacío
      // Si el estado tiene puntos, usar el estado
      // Si el estado NO tiene puntos pero storage SÍ tiene, mantener los de storage
      const puntosParaGuardar = (puntosEntrega && puntosEntrega.length > 0) 
        ? puntosEntrega 
        : (dataEnStorage?.puntosEntrega || [])
      
      // Mismo para otros campos
      const rutaParaGuardar = (ruta && ruta.length > 0) 
        ? ruta 
        : (dataEnStorage?.ruta || [])
      
      const puntoInicialParaGuardar = puntoInicial || dataEnStorage?.puntoInicial || null
      
      // Para IDs: usar el Map si tiene datos, sino usar los de storage
      const idsParaGuardar = idsEntregaRef.current.size > 0
        ? Object.fromEntries(idsEntregaRef.current)
        : (dataEnStorage?.idsEntrega || {})
      
      const posicionParaGuardar = posicionRepartidor || dataEnStorage?.posicionRepartidor || null
      const simulandoParaGuardar = simulando !== undefined ? simulando : (dataEnStorage?.simulando || false)
      
      const datosParaGuardar = {
        puntosEntrega: puntosParaGuardar,
        ruta: rutaParaGuardar,
        puntoInicial: puntoInicialParaGuardar,
        idsEntrega: idsParaGuardar,
        posicionRepartidor: posicionParaGuardar,
        simulando: simulandoParaGuardar
      }
      
      // Debug: verificar qué se está guardando
      if (puntosParaGuardar.length > 0) {
        console.log('🟢 Guardando en sessionStorage (contexto):', {
          puntosCount: datosParaGuardar.puntosEntrega.length,
          idsCount: Object.keys(datosParaGuardar.idsEntrega).length,
          primerPunto: datosParaGuardar.puntosEntrega[0]?.id,
          desdeEstado: puntosEntrega && puntosEntrega.length > 0,
          desdeStorage: !puntosEntrega || puntosEntrega.length === 0
        })
      }
      
      localStorage.setItem('turutatrack_simulacion', JSON.stringify(datosParaGuardar))
    } catch (error) {
      console.error('Error guardando en sessionStorage:', error)
    }
  }, [puntosEntrega, ruta, puntoInicial, posicionRepartidor, simulando])

  // Sincronizar también cuando cambien los IDs
  const actualizarIdsEnStorage = () => {
    try {
      const stored = localStorage.getItem('turutatrack_simulacion')
      if (stored) {
        const data = JSON.parse(stored)
        data.idsEntrega = Object.fromEntries(idsEntregaRef.current)
        sessionStorage.setItem('turutatrack_simulacion', JSON.stringify(data))
      }
    } catch (error) {
      console.error('Error actualizando IDs en sessionStorage:', error)
    }
  }

  /**
   * Genera o recupera el ID único para un punto de entrega
   */
  const obtenerIdEntrega = (index) => {
    if (!idsEntregaRef.current.has(index)) {
      // Generar ID único usando timestamp + índice + random
      const id = `entrega-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
      idsEntregaRef.current.set(index, id)
      // Guardar inmediatamente en sessionStorage
      actualizarIdsEnStorage()
    }
    return idsEntregaRef.current.get(index)
  }

  /**
   * Obtiene el índice de entrega por su ID
   */
  const obtenerIndicePorId = (id) => {
    for (const [index, entregaId] of idsEntregaRef.current.entries()) {
      if (entregaId === id) {
        return index
      }
    }
    return -1
  }

  /**
   * Limpia todos los IDs (cuando se reinicia la simulación)
   */
  const limpiarIds = () => {
    idsEntregaRef.current.clear()
    // También limpiar sessionStorage
    try {
      sessionStorage.removeItem('turutatrack_simulacion')
    } catch (error) {
      console.error('Error limpiando sessionStorage:', error)
    }
  }

  const value = {
    // Estado
    posicionRepartidor,
    setPosicionRepartidor,
    ruta,
    setRuta,
    puntosEntrega,
    setPuntosEntrega,
    puntoInicial,
    setPuntoInicial,
    simulando,
    setSimulando,
    velocidad,
    setVelocidad,
    // Funciones
    obtenerIdEntrega,
    obtenerIndicePorId,
    limpiarIds
  }

  return (
    <SimulacionContext.Provider value={value}>
      {children}
    </SimulacionContext.Provider>
  )
}

/**
 * Hook para usar el contexto de simulación
 */
export function useSimulacion() {
  const context = useContext(SimulacionContext)
  if (!context) {
    throw new Error('useSimulacion debe usarse dentro de SimulacionProvider')
  }
  return context
}

